import React, { useCallback, useState } from 'react'
import { browserHistory } from 'utils/navigation'
import PropTypes from 'prop-types'
import { Button, Container, Input, Checkbox, Popup, SubMenu, Divider, Radio } from 'components/UIElements'
import '../stylesheets/createStudy.scss'
import {
  isDeployRegionChina,
  STUDY_CONFIG_MAP,
  REWARD_TYPE_MAP,
  REWARD_NAME_MAP,
  DURATION_TYPE_MAP,
  DAYS_IN_WEEK,
  MIN_SESSION_TIMEOUT_MINUTES,
} from 'utils/constants'
import STRINGS from 'utils/strings'
import { pluralize } from 'utils/misc'
import { checkIsEmailless } from 'utils/studyConfigUtils'
import {
  STUDY_CONFIGURATION_EDITABLE_FIELD_IDS,
  isStudyConfigurationFieldEditable,
} from 'utils/studyConfigurationEditability'
import { confettiIcon, supriseBox, gem, virtualVisitsIcon, visitSupportIcon } from 'assets/assets'
import { CREATE_STUDY_FULL_URLS, generatePClassName } from '../utils/studyUtils'
import {
  parseFormatString,
  generateFormatString,
  findFieldByKey,
  ensureFieldExists,
  updateField,
  relocateFieldByRequired,
} from './idFormatUtils'
import {
  PRESCREEN_ID_KEY,
  PRESCREEN_ID_LABEL,
  SUBJECT_ID_KEY,
  SUBJECT_ID_LABEL,
  UI_TEXT,
  REVIEW_SCREEN_TEXT,
} from '../utils/constants'
import { Suspense, lazy } from 'react'
import { Loader } from 'components/UIElements'
import AppPushConfig from './AppPushConfig'

const IDFormatBuilder = lazy(() => import(/* webpackChunkName: "dnd-kit-idformat" */ './IDFormatBuilder'))
import CycleRewardConfig from './CyclesRewardConfig'
import BinaryConfig from './BinaryConfig'
import DynamicCycleRewardConfig from './DynamicCycleRewardConfig'
import EnforcedLanguagesConfig from './EnforcedLanguagesConfig'
import RewardConfig from './RewardConfig'
import SecurityConfig from './SecurityConfig'
import StudyWizardHeader from './StudyWizardHeader'
import NewRewardsConfig from './NewRewardsConfig'
import GiftCardConfig from './GiftCardConfig'
import RewardSelectionConfig from './RewardSelectionConfig'
import CreateStudyFooter from './CreateStudyFooter'
import ParticipantOfflineDuration from './ParticipantOfflineDuration'
import { EmaillessConfig, MultiScheduleConfigStatus } from './CreateStudyType'
import FlagsmithConfigurationSection from './FlagsmithConfigurationSection'
import StudyVersionDetailsSection from './StudyVersionDetailsSection'

const richButtonIconMap = {
  ban: <i className='fas fa-ban' />,
  squareCheck: <i className='fas fa-check-square' />,
  dataSync: (
    <div className='data-sync-icon flexed column'>
      <div />
    </div>
  ),
  confetti: confettiIcon,
  iconContainer: icon => {
    return (
      <div className='mid-term-reward-icon flexed column end-justified'>
        <div>{icon()}</div>
      </div>
    )
  },
}

const CreateStudyReview = props => {
  const {
    disabled,
    isRootOrDchAdmin,
    isWizard,
    saveAttempted,
    updateStudy,
    study,
    updateConfig,
    isFlagsmithConfigurationSectionShown = false,
    hasStudyPromotion = false,
    userId,
    openModal,
    closeModal,
    studyVersionList,
    fetchStudyVersionList,
    promoteStudyToUat,
    promoteStudyToLive,
    saveStudyVersionNotes,
    hasAccessToEditVersionNotes,
    studyVersionID,
    showError,
    patchStudy,
    fetchStudy,
    canEditStudyConfiguration = false,
  } = props

  const configObj = study.config || study

  const [inlineEditFieldId, setInlineEditFieldId] = useState(null)
  const [sessionDraftMinutes, setSessionDraftMinutes] = useState('')
  const [visitTimeDraft, setVisitTimeDraft] = useState(false)
  const [savingInlineConfig, setSavingInlineConfig] = useState(false)

  const persistConfigPatch = useCallback(
    configPatch => {
      if (!patchStudy || !study?.id) return Promise.resolve()
      setSavingInlineConfig(true)
      return patchStudy({
        studyId: study.id,
        studyDiff: {
          config: {
            ...configObj,
            ...configPatch,
          },
        },
        diffIsConfig: true,
      })
        .then(() => (fetchStudy ? fetchStudy(study.id) : undefined))
        .finally(() => {
          setSavingInlineConfig(false)
          setInlineEditFieldId(null)
        })
    },
    [patchStudy, fetchStudy, study?.id, configObj],
  )

  const cancelInlineConfigEdit = useCallback(() => {
    setInlineEditFieldId(null)
    if (fetchStudy && study?.id) fetchStudy(study.id)
  }, [fetchStudy, study?.id])

  const showConfigPencil = fieldId =>
    disabled && canEditStudyConfiguration && !!patchStudy && isStudyConfigurationFieldEditable(fieldId)

  const updateStudyConfig = newConfig => {
    const config = study.config || {}
    const _config = config || {}
    updateConfig({
      ..._config,
      ...{ ...newConfig },
    })
  }

  const deleteConfigKey = key => {
    const config = study.config || {}
    if (!config) return
    const newConfig = config
    delete newConfig[key]
    updateConfig(newConfig)
  }

  const getHoursAndMins = mins => {
    const hours = Math.floor(mins / 60)
    const leftMins = mins % 60
    return [hours, leftMins]
  }

  const { self_sign_code } = study
  const {
    admin_security,
    consent_required,
    cycles,
    data_sync_ui_enabled,
    enforced_language,
    forced_instrument_priority,
    has_dynamic_cycles,
    hasEnforcedLanguage,
    neutral_reward_enabled,
    session_timeout_seconds,
    training,
    visit_support,
    virtual_visits,
    study_promotion,
  } = configObj

  const isEmailless = checkIsEmailless(study)

  const isChina = isDeployRegionChina()

  const configProps = {
    ...props,
    configObj,
    deleteConfigKey,
    updateConfig: updateStudyConfig,
  }
  const giftCardSelected = configObj[STUDY_CONFIG_MAP.treasureChestStoreGiftCard]

  const hasLanguageError =
    saveAttempted && hasEnforcedLanguage && !!enforced_language && enforced_language.default_language === ''

  const hasGemsRewards = [REWARD_TYPE_MAP.gems, REWARD_TYPE_MAP.superGems].includes(configObj?.cycles?.[0].rewardType)

  const getFormattedCycles = () => {
    let _cycles = cycles
    if (!cycles) _cycles = study.cycles
    if (has_dynamic_cycles) {
      return _cycles
    }
    if (!_cycles) return []

    const cycle = _cycles[0]
    const duration = Math.ceil(
      (cycle?.durationType === DURATION_TYPE_MAP.weeks ? cycle?.studyDuration * DAYS_IN_WEEK : cycle?.studyDuration) /
      cycle?.cycleDuration,
    )
    const newArr = Array.from({ length: duration }, () => ({}))
    return newArr.map((_, idx) => ({
      ...cycle,
      cycleDuration:
        idx + 1 === duration && cycle?.studyDuration % cycle?.cycleDuration
          ? cycle?.studyDuration % cycle?.cycleDuration
          : cycle?.cycleDuration,
    }))
  }

  const studyCycles = getFormattedCycles()

  const renderStudyNameInput = () => {
    return (
      <Input
        className='name'
        hasError={saveAttempted && study.platform_name === ''}
        errorText={STRINGS.cannotBeEmpty}
        label={STRINGS.studyName}
        disabled={disabled}
        value={study.platform_name}
        onChange={value => updateStudy('platform_name', value)}
        id='study-name'
        placeholder={STRINGS.studyName}
      />
    )
  }

  const renderAppNameInput = () => {
    return (
      <Input
        charLimit={60}
        className='name pos rel'
        hasError={saveAttempted && study.name === ''}
        errorText={STRINGS.cannotBeEmpty}
        label='Name displayed to participants in the app'
        disabled={disabled}
        value={study.name}
        onChange={value => updateStudy('name', value)}
        id='app-display-name'
        placeholder='App Display Name'
      />
    )
  }

  const renderTimeoutInput = sessionTimeoutConfig => {
    const { updateValue, updateStudySecurityConfig, key, currentSettings } = sessionTimeoutConfig || {}
    const cfg = study.config || study
    const { admin_security, session_timeout_seconds } = cfg
    const onChange = sessionTimeoutConfig
      ? updateValue(updateStudySecurityConfig, key, currentSettings)
      : val => updateStudyConfig({ session_timeout_seconds: val * 60 })
    const adminSessionTimeout =
      admin_security?.[key] ?? admin_security?.session_timeout ?? admin_security?.timeout_flag
    const sessionValue = sessionTimeoutConfig
      ? disabled &&
        (adminSessionTimeout === undefined || adminSessionTimeout === null || adminSessionTimeout === '')
        ? '30'
        : adminSessionTimeout || ''
      : session_timeout_seconds
        ? session_timeout_seconds / 60
        : ''
    const [hours, mins] = getHoursAndMins(sessionValue)
    const exampleTime =
      hours > 0 ? ` ( ${pluralize(hours, 'hour', 'hours')} ${mins > 0 ? pluralize(mins, 'min ', 'mins ') : ''}) ` : ' '
    return (
      <Input
        className='user-session-timeout'
        disabled={disabled}
        inline
        onChange={onChange}
        pretext='Timeout after'
        posttext={`minutes${exampleTime}`}
        preventInput={num => num < 0}
        id='session-timeout'
        type='number'
        value={sessionValue}
      />
    )
  }

  const openParticipantSessionInlineEdit = () => {
    const sec = configObj.session_timeout_seconds
    const hasSec = sec !== undefined && sec !== null && sec !== ''
    const fromSec = hasSec ? Math.round(Number(sec) / 60) : ''
    const fallbackMin = configObj.admin_security?.session_timeout
    const displayMin = fromSec !== '' ? fromSec : fallbackMin ?? ''
    setSessionDraftMinutes(String(displayMin))
    setInlineEditFieldId(STUDY_CONFIGURATION_EDITABLE_FIELD_IDS.participantSessionTimeout)
  }

  const saveParticipantSessionInline = () => {
    let mins = Number(sessionDraftMinutes)
    if (Number.isNaN(mins) || mins < MIN_SESSION_TIMEOUT_MINUTES) {
      mins = MIN_SESSION_TIMEOUT_MINUTES
    }
    return persistConfigPatch({ session_timeout_seconds: mins * 60 })
  }

  const openVisitTimeInlineEdit = () => {
    setVisitTimeDraft(!!configObj?.visit_support?.enable_visit_time)
    setInlineEditFieldId(STUDY_CONFIGURATION_EDITABLE_FIELD_IDS.visitSupportEnableVisitTime)
  }

  const saveVisitTimeInline = () => {
    const visitSupportConfig = configObj?.visit_support || {}
    return persistConfigPatch({
      visit_support: { ...visitSupportConfig, enable_visit_time: visitTimeDraft },
    })
  }

  const configCheckbox = ({ id, label, coupledKeyString, keyString, toggle = false, val = {} }) => {
    return (
      <Checkbox
        id={id}
        toggle={toggle}
        label={label}
        checked={!!configObj[keyString]}
        disabled={disabled}
        onClick={() => {
          const enabled = configObj[keyString]
          if (!enabled) {
            updateStudyConfig({ [keyString]: val })
            if (coupledKeyString) updateStudyConfig({ [coupledKeyString]: val })
          } else {
            deleteConfigKey(keyString)
            if (coupledKeyString) deleteConfigKey(coupledKeyString)
          }
        }}
      />
    )
  }

  const NameSection = (
    <Container>
      {isWizard ? <StudyWizardHeader /> : <h3>{disabled ? STRINGS.studyInfoViewOnly : null}</h3>}
      {self_sign_code && (
        <div className='flexed start-justified start-aligned'>
          <p>{`Study Sign-up Code: ${self_sign_code}`}</p>
          <Popup hover noPointer dark position='right' trigger={<i className='fas fa-info-circle' />}>
            <p>{STRINGS.selfSignUpCode}</p>
          </Popup>
        </div>
      )}
      {renderStudyNameInput()}
      <Checkbox
        id='mark-as-test-study'
        label='Mark as Test Study'
        checked={configObj.is_test}
        disabled={disabled}
        onClick={() => updateStudyConfig({ is_test: !configObj.is_test })}
      />
      {renderAppNameInput()}
    </Container>
  )

  const orderedInstrumentContent = (isOrdered = true) => {
    return isOrdered ? (
      <div id='ordered-instruments'>
        <p>{STRINGS.orderedInstrumentsHeading}</p>
        <p>{STRINGS.orderedInstrumentsBody}</p>
      </div>
    ) : (
      <div id='unordered-instruments'>
        <p>{STRINGS.unorderedInstrumentsHeading}</p>
        <p>{STRINGS.unorderedInstrumentsBody}</p>
      </div>
    )
  }

  const MainFeaturesSection = (
    <Container>
      <h3>{STRINGS.studyConfigurations}</h3>
      {disabled && <EmaillessConfig disabled={disabled} isEmailless={isEmailless} />}
      {disabled && <MultiScheduleConfigStatus configObj={configObj} />}
      <h5>{STRINGS.features}</h5>
      <div className='config-checkboxes'>
        <div className='checkboxes'>
          {!isChina && (
            <>
              <Checkbox
                id='econsent'
                label={STRINGS.eConsent}
                checked={configObj.consent_required}
                disabled={disabled}
                onClick={() => updateStudyConfig({ consent_required: !configObj.consent_required })}
              />
              <p className={generatePClassName(configObj, STUDY_CONFIG_MAP.consentRequired, disabled)}>
                {STRINGS.eConsentConfigBody}
              </p>
            </>
          )}
          <Checkbox
            id='training'
            label={STRINGS.training}
            checked={configObj.training?.enabled}
            disabled={disabled}
            onClick={() => updateStudyConfig({ training: { enabled: !configObj.training?.enabled } })}
          />
          <p className={generatePClassName(configObj, [STUDY_CONFIG_MAP.training, 'enabled'], disabled)}>
            {STRINGS.trainingConfigBody}
          </p>
          {isRootOrDchAdmin && (
            <>
              {configCheckbox({
                id: 'clinician-reported-outcome',
                label: STRINGS.clinicianReportedOutcomes,
                keyString: STUDY_CONFIG_MAP.clinro,
                coupledKeyString: STUDY_CONFIG_MAP.virtualVisits,
              })}
              <p className={generatePClassName(configObj, STUDY_CONFIG_MAP.clinro, disabled)}>
                {STRINGS.clinroConfigBody}
              </p>
              {configCheckbox({
                id: 'medication-adherance',
                label: STRINGS.medicationAdherence,
                keyString: STUDY_CONFIG_MAP.medicationAdherence,
              })}
              <p className={generatePClassName(configObj, STUDY_CONFIG_MAP.medicationAdherence, disabled)}>
                {STRINGS.medicationAdherenceConfigBody}
              </p>
              {configCheckbox({
                id: 'study-promotion',
                label: STRINGS.studyPromotion,
                keyString: STUDY_CONFIG_MAP.studyPromotion,
              })}
              <p className={generatePClassName(configObj, STUDY_CONFIG_MAP.studyPromotion, disabled)}>
                {STRINGS.studyPromotionConfigBody}
              </p>
              <div className='config-toggles flexed column start-aligned'>
                {configCheckbox({
                  id: 'advanced-features',
                  label: STRINGS.advancedFeatures,
                  keyString: STUDY_CONFIG_MAP.hasAdvancedFeatures,
                  toggle: true,
                  val: true,
                })}
                {configObj.hasAdvancedFeatures && (
                  <SubMenu>
                    {configCheckbox({
                      id: 'custom-onboarding',
                      label: STRINGS.customOnboarding,
                      keyString: STUDY_CONFIG_MAP.customOnboarding,
                    })}
                    {configCheckbox({
                      id: 'case-management',
                      label: STRINGS.caseManagement,
                      keyString: STUDY_CONFIG_MAP.caseManagement,
                    })}
                    {!isChina &&
                      configCheckbox({
                        id: 'geofence-triggered',
                        label: STRINGS.geofenceInstruments,
                        keyString: STUDY_CONFIG_MAP.customGeofenceBehavior,
                      })}
                    {configCheckbox({
                      id: 'custom-report-outcome',
                      label: STRINGS.customReportButton,
                      keyString: STUDY_CONFIG_MAP.customMapButton,
                    })}
                  </SubMenu>
                )}
              </div>
            </>
          )}
          <h5>{STRINGS.visitConfiguration}</h5>
          <div className='flexed checkbox-with-graphic'>
            <div>
              {configCheckbox({
                id: 'visit-support-checkbox',
                label: STRINGS.visitSupport,
                keyString: STUDY_CONFIG_MAP.visitSupport,
                val: { enable_visit_time: false },
              })}
              <p className={generatePClassName(configObj, STUDY_CONFIG_MAP.clinro, disabled)}>
                {STRINGS.visitSupportConfigBody}
              </p>
              {configObj?.visit_support && (
                <div className='visit-support-suboption'>
                  <div className='config-inline-edit-row'>
                    <Checkbox
                      id={'record-visit-time-checkbox'}
                      toggle={false}
                      label={STRINGS.visitTimeVisibility}
                      checked={
                        inlineEditFieldId === STUDY_CONFIGURATION_EDITABLE_FIELD_IDS.visitSupportEnableVisitTime
                          ? visitTimeDraft
                          : !!configObj?.visit_support?.enable_visit_time
                      }
                      disabled={
                        disabled &&
                        inlineEditFieldId !== STUDY_CONFIGURATION_EDITABLE_FIELD_IDS.visitSupportEnableVisitTime
                      }
                      onClick={() => {
                        const visitSupportConfig = configObj?.visit_support || {}
                        if (
                          inlineEditFieldId === STUDY_CONFIGURATION_EDITABLE_FIELD_IDS.visitSupportEnableVisitTime
                        ) {
                          setVisitTimeDraft(v => !v)
                          return
                        }
                        const enabled = !!visitSupportConfig.enable_visit_time
                        updateStudyConfig({
                          visit_support: { ...visitSupportConfig, enable_visit_time: !enabled },
                        })
                      }}
                    />
                    {showConfigPencil(STUDY_CONFIGURATION_EDITABLE_FIELD_IDS.visitSupportEnableVisitTime) &&
                      inlineEditFieldId !== STUDY_CONFIGURATION_EDITABLE_FIELD_IDS.visitSupportEnableVisitTime && (
                        <Button
                          link
                          id='edit-visit-time-visibility'
                          icon='fas fa-pencil-alt'
                          content=''
                          onClick={openVisitTimeInlineEdit}
                        />
                      )}
                  </div>
                  {inlineEditFieldId === STUDY_CONFIGURATION_EDITABLE_FIELD_IDS.visitSupportEnableVisitTime && (
                    <div className='config-inline-edit-actions'>
                      <Button
                        id='save-visit-time-visibility'
                        content='Save'
                        disabled={savingInlineConfig}
                        onClick={saveVisitTimeInline}
                      />
                      <Button
                        link
                        id='cancel-visit-time-visibility'
                        content='Cancel'
                        disabled={savingInlineConfig}
                        onClick={cancelInlineConfigEdit}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            {visitSupportIcon({ className: 'checkbox-graphic' })}
          </div>
          {configObj?.visit_support && (
            <>
              <div className='flexed checkbox-with-graphic'>
                <div>
                  <Checkbox
                    id={'visit-cycling-checkbox'}
                    toggle={false}
                    label={STRINGS.visitCycling}
                    checked={configObj?.visit_support?.version === 2}
                    disabled={disabled}
                  />
                  <p className={generatePClassName(configObj, STUDY_CONFIG_MAP.clinro, disabled)}>
                    {STRINGS.visitCyclingConfigBody}
                  </p>
                </div>
                {visitSupportIcon({ className: 'checkbox-graphic' })}
              </div>
            </>
          )}
          {isRootOrDchAdmin && (
            <div className='flexed checkbox-with-graphic'>
              <div>
                <Checkbox
                  id={'virtual-visit-checkbox'}
                  toggle={false}
                  label={STRINGS.virtualVisitClinro}
                  checked={!!configObj?.virtual_visits}
                  disabled={disabled}
                />
                <p className={generatePClassName(configObj, STUDY_CONFIG_MAP.clinro, disabled)}>
                  {STRINGS.virtualVisitConfigBody}
                </p>
              </div>
              {virtualVisitsIcon({ className: 'checkbox-graphic' })}
            </div>
          )}
          <Divider color='#dfe1f1' />
        </div>
        <h5>{STRINGS.instrumentCompletionOrder}</h5>
        <div className='flexed instrument-priority'>
          <Radio
            disabled={disabled}
            selected={configObj.forced_instrument_priority}
            onClick={() => updateStudyConfig({ [STUDY_CONFIG_MAP.forcedInstrumentPriority]: true })}
            content={orderedInstrumentContent(true)}
          />
          <Radio
            disabled={disabled}
            selected={!configObj.forced_instrument_priority}
            onClick={() => updateStudyConfig({ [STUDY_CONFIG_MAP.forcedInstrumentPriority]: false })}
            content={orderedInstrumentContent(false)}
          />
        </div>
        <Divider color='#dfe1f1' />
        <AppPushConfig {...configProps} />
      </div>
    </Container>
  )

  const MainFeaturesReadOnlySection = (
    <Container className='read-only-section'>
      <div className='heading-row'>
        <p>Study Configuration</p>
        <Button
          link
          onClick={() => browserHistory.push(CREATE_STUDY_FULL_URLS.type)}
          id='edit-button'
          icon='fas fa-pencil-alt'
          content='Edit'
        />
      </div>
      <div className='content-row'>
        <p>{STRINGS.ptpContactInformationCollection}</p>
        <p>{!isEmailless ? 'Enabled' : 'Disabled'}</p>
      </div>
      <div className='content-row'>
        <p>{STRINGS.eConsent}</p>
        <p>{consent_required ? STRINGS.yes : STRINGS.no}</p>
      </div>
      <div className='content-row'>
        <p>{STRINGS.training}</p>
        <p>{training?.enabled ? STRINGS.yes : STRINGS.no}</p>
      </div>
      <div className='content-row'>
        <p>{STRINGS.studyPromotion}</p>
        <p>{study_promotion ? STRINGS.yes : STRINGS.no}</p>
      </div>
      <div className='content-row'>
        <p>{STRINGS.visitSupport}</p>
        <p>{visit_support ? STRINGS.yes : STRINGS.no}</p>
      </div>
      <div className='content-row'>
        <p>{STRINGS.visitTimeVisibility}</p>
        <div className='inline-edit-value'>
          <p>{visit_support?.enable_visit_time ? STRINGS.yes : STRINGS.no}</p>
        </div>
      </div>
      <div className='content-row'>
        <p>{STRINGS.visitCycling}</p>
        <p>{visit_support?.version === 2 ? STRINGS.yes : STRINGS.no}</p>
      </div>
      <div className='content-row'>
        <p>{STRINGS.virtualVisits}</p>
        <p>{!!virtual_visits ? STRINGS.yes : STRINGS.no}</p>
      </div>
      <div className='content-row last'>
        <p>{STRINGS.instrumentOrder}</p>
        <p>{forced_instrument_priority ? STRINGS.Ordered : STRINGS.unordered}</p>
      </div>
    </Container>
  )

  // Helper function to format ID configuration for display
  const formatIdConfiguration = (idKey, idLabel, enabledKey) => {
    // Check if the ID type is enabled
    const isEnabled = !!configObj[enabledKey]
    if (!isEnabled) return null

    const field = findFieldByKey(configObj.participant_data_fields, idKey)
    if (!field) return null

    const isAutoGen = field?.metadata?.is_auto_generated || false
    const isRequired = (configObj.participant_data_fields?.mandatory || []).some(
      f => f?.metadata?.internal_key === idKey,
    )
    const formatString = field?.metadata?.auto_gen_format || ''

    return {
      label: idLabel,
      isEnabled: true,
      isAutoGenerate: isAutoGen,
      isRequired: isRequired,
      formatString: formatString,
    }
  }

  const renderParticipantReadOnlySection = () => {
    const sessionTimeOutSeconds = session_timeout_seconds
    const sessionTimeoutMin = Math.round(sessionTimeOutSeconds / 60) || admin_security?.session_timeout

    // Get ID configurations
    const prescreenConfig = formatIdConfiguration(PRESCREEN_ID_KEY, PRESCREEN_ID_LABEL, 'pre_screen_id_enabled')
    const subjectConfig = formatIdConfiguration(SUBJECT_ID_KEY, SUBJECT_ID_LABEL, 'subject_id_enabled')
    return (
      <Container className='read-only-section'>
        <div className='heading-row'>
          <p>{STRINGS.ptpAppSettings}</p>
          <Button
            link
            onClick={() => browserHistory.push(CREATE_STUDY_FULL_URLS.setting)}
            id='edit-button'
            icon='fas fa-pencil-alt'
            content='Edit'
          />
        </div>
        <div className='content-row'>
          <p>Timeout minutes</p>
          <p>{sessionTimeoutMin ? `${pluralize(sessionTimeoutMin, 'minute', 'minutes')}` : 'None'}</p>
        </div>
        {prescreenConfig && (
          <>
            <div className='content-row'>
              <p>{prescreenConfig.label}</p>
              <div className='id-config-display'>
                <p>
                  {REVIEW_SCREEN_TEXT.MODE_LABEL}{' '}
                  {prescreenConfig.isAutoGenerate ? UI_TEXT.MODE_AUTOGENERATE : UI_TEXT.MODE_MANUAL}
                </p>
                <p>
                  {REVIEW_SCREEN_TEXT.REQUIRED_LABEL}{' '}
                  {prescreenConfig.isRequired ? REVIEW_SCREEN_TEXT.YES : REVIEW_SCREEN_TEXT.NO}
                </p>
                <p>
                  {REVIEW_SCREEN_TEXT.FORMAT_LABEL}{' '}
                  {prescreenConfig.formatString || REVIEW_SCREEN_TEXT.NO_FORMAT_DEFINED}
                </p>
              </div>
            </div>
          </>
        )}
        {subjectConfig && (
          <>
            <div className='content-row'>
              <p>{subjectConfig.label}</p>
              <div className='id-config-display'>
                <p>
                  {REVIEW_SCREEN_TEXT.MODE_LABEL}{' '}
                  {subjectConfig.isAutoGenerate ? UI_TEXT.MODE_AUTOGENERATE : UI_TEXT.MODE_MANUAL}
                </p>
                <p>
                  {REVIEW_SCREEN_TEXT.REQUIRED_LABEL}{' '}
                  {subjectConfig.isRequired ? REVIEW_SCREEN_TEXT.YES : REVIEW_SCREEN_TEXT.NO}
                </p>
                <p>
                  {REVIEW_SCREEN_TEXT.FORMAT_LABEL} {subjectConfig.formatString || REVIEW_SCREEN_TEXT.NO_FORMAT_DEFINED}
                </p>
              </div>
            </div>
          </>
        )}
        <div className='content-row'>
          <p>Tone of voice</p>
          <p>{neutral_reward_enabled ? 'Neutral' : 'Celebratory'}</p>
        </div>
        <div className='content-row'>
          <p>Data Sync Indicator</p>
          <p>{data_sync_ui_enabled ? 'Show' : 'Hide'}</p>
        </div>
        <div className='content-row last'>
          <p>Expire admin and clinician passwords</p>
          <p>{admin_security?.expiration.isToggled ? 'Expire passwords' : 'Unexpire passowrds'}</p>
        </div>
      </Container>
    )
  }

  const ScheduleAndRewardsReadOnlySection = (
    <Container className='read-only-section'>
      <div className='heading-row'>
        <p>Schedule & Rewards</p>
        <Button
          link
          onClick={() => browserHistory.push(CREATE_STUDY_FULL_URLS.schedule)}
          id='edit-button'
          icon='fas fa-pencil-alt'
          content='Edit'
        />
      </div>
      <div className='subheading-row'>
        <p>Timeline</p>
      </div>
      {studyCycles?.map((cycle, idx) => (
        <div className={`content-row ${idx + 1 === studyCycles?.length ? 'last' : ''}`} key={idx}>
          <p>{`Chapter ${idx + 1}`}</p>
          <p>{`${cycle?.cycleDuration} ${cycle?.cycleDuration === 1 ? 'day' : 'days'}, ${cycle?.target
            } required instruments`}</p>
        </div>
      ))}
      <div className='subheading-row'>
        <p>Rewards</p>
      </div>
      {studyCycles?.map((cycle, idx) => (
        <div className={`content-row ${idx + 1 === studyCycles?.length ? 'last' : ''}`} key={idx}>
          <p>{`Chapter ${idx + 1}`}</p>
          <p>{`${cycle?.rewardType === REWARD_TYPE_MAP.cash ? cycle?.reward / 100 : cycle?.reward} ${REWARD_NAME_MAP[cycle?.rewardType]
            }`}</p>
        </div>
      ))}
    </Container>
  )

  // Get Pre-Screen ID field configuration
  const prescreenField = findFieldByKey(configObj.participant_data_fields, PRESCREEN_ID_KEY)
  const prescreenIsAutoGen = prescreenField?.metadata?.is_auto_generated || false
  const prescreenIsRequired = prescreenField
    ? (configObj.participant_data_fields?.mandatory || []).some(f => f?.metadata?.internal_key === PRESCREEN_ID_KEY)
    : true
  const prescreenFormatString = prescreenField?.metadata?.auto_gen_format || ''
  const prescreenPattern = parseFormatString(prescreenFormatString)

  // Get Subject ID field configuration
  const subjectField = findFieldByKey(configObj.participant_data_fields, SUBJECT_ID_KEY)
  const subjectIsAutoGen = subjectField?.metadata?.is_auto_generated || false
  const subjectIsRequired = subjectField
    ? (configObj.participant_data_fields?.mandatory || []).some(f => f?.metadata?.internal_key === SUBJECT_ID_KEY)
    : true
  const subjectFormatString = subjectField?.metadata?.auto_gen_format || ''
  const subjectPattern = parseFormatString(subjectFormatString)

  const handlePatternChange = (internalKey, displayLabel, isRequired) => pattern => {
    const formatString = generateFormatString(pattern)
    let newFields = ensureFieldExists(configObj.participant_data_fields, internalKey, displayLabel, isRequired)
    newFields = updateField(newFields, internalKey, { auto_gen_format: formatString })
    updateStudyConfig({ participant_data_fields: newFields })
  }

  const handleModeChange = (internalKey, displayLabel, currentIsRequired) => changes => {
    let newFields = ensureFieldExists(
      configObj.participant_data_fields,
      internalKey,
      displayLabel,
      changes.isRequired !== undefined ? changes.isRequired : currentIsRequired,
    )

    const updates = {}
    if (changes.isAutoGenerate !== undefined) {
      updates.is_auto_generated = changes.isAutoGenerate
    }

    newFields = updateField(newFields, internalKey, updates)
    if (changes.isRequired !== undefined) {
      newFields = relocateFieldByRequired(newFields, internalKey, changes.isRequired)
    }

    updateStudyConfig({
      participant_data_fields: newFields,
    })
  }

  const idFormatFallback = <div className='flexed center-justified' style={{ minHeight: 80 }}><Loader inContainer size={24} /></div>
  const prescreenIdConfig = configObj.pre_screen_id_enabled && (
    <div className='id-configuration-section'>
      <h5>{PRESCREEN_ID_LABEL}</h5>
      <Suspense fallback={idFormatFallback}>
        <IDFormatBuilder
          idType='prescreen'
          isAutoGenerate={prescreenIsAutoGen}
          isRequired={prescreenIsRequired}
          pattern={prescreenPattern}
          onChange={handlePatternChange(PRESCREEN_ID_KEY, PRESCREEN_ID_LABEL, prescreenIsRequired)}
          onModeChange={handleModeChange(PRESCREEN_ID_KEY, PRESCREEN_ID_LABEL, prescreenIsRequired)}
          disabled={disabled}
        />
      </Suspense>
    </div>
  )

  const subjectIdConfig = (
    <div>
      <h5>{STRINGS.ptpSubjectId}</h5>
      <p className={generatePClassName(configObj, STUDY_CONFIG_MAP.showParticipantUniqueIdentifier, disabled)}>
        {STRINGS.subjectId}
      </p>
      {configCheckbox({
        id: 'subject-id',
        label: STRINGS.showPtpSubjectId,
        keyString: STUDY_CONFIG_MAP.showParticipantUniqueIdentifier,
        toggle: false,
        val: {
          type: 'subject_id?subject_id:participant_id',
        },
      })}
      {configObj.subject_id_enabled && (
        <div className='id-configuration-section'>
          <h5>Subject ID Configuration</h5>
          <Suspense fallback={idFormatFallback}>
            <IDFormatBuilder
              idType='subject'
              isAutoGenerate={subjectIsAutoGen}
              isRequired={subjectIsRequired}
              pattern={subjectPattern}
              onChange={handlePatternChange(SUBJECT_ID_KEY, SUBJECT_ID_LABEL, subjectIsRequired)}
              onModeChange={handleModeChange(SUBJECT_ID_KEY, SUBJECT_ID_LABEL, subjectIsRequired)}
              disabled={disabled}
            />
          </Suspense>
        </div>
      )}
    </div>
  )

  const endOfStudyConfig = (
    <div>
      <h5>{STRINGS.endOfStudySummary}</h5>
      <p className={generatePClassName(configObj, STUDY_CONFIG_MAP.studyEnd, disabled)}>
        {STRINGS.endOfStudyConfigBody}
      </p>
      {configCheckbox({
        id: 'end-of-summary',
        label: STRINGS.displayEndOfStudy,
        keyString: STUDY_CONFIG_MAP.studyEnd,
      })}
    </div>
  )

  const ParticipantSection = (
    <Container>
      <h3>{STRINGS.ptpAppSettings}</h3>
      {isRootOrDchAdmin && (
        <>
          <h5>{STRINGS.languages}</h5>
          <EnforcedLanguagesConfig
            {...props}
            resetEnforcedLanguage={() => updateStudyConfig({ hasEnforcedLanguage: false })}
            updateConfig={updateStudyConfig}
            hasError={hasLanguageError}
          />
          <Divider color='#dfe1f1' />
          {prescreenIdConfig}
          <Divider color='#dfe1f1' />
          {subjectIdConfig}
          <Divider color='#dfe1f1' />
          {endOfStudyConfig}
          <Divider color='#dfe1f1' />
        </>
      )}
      <div className='config-inline-edit-heading'>
        <h5>Session Time Out</h5>
        {showConfigPencil(STUDY_CONFIGURATION_EDITABLE_FIELD_IDS.participantSessionTimeout) &&
          inlineEditFieldId !== STUDY_CONFIGURATION_EDITABLE_FIELD_IDS.participantSessionTimeout && (
            <Button
              link
              id='edit-participant-session-timeout'
              icon='fas fa-pencil-alt'
              content=''
              onClick={openParticipantSessionInlineEdit}
            />
          )}
      </div>
      {inlineEditFieldId === STUDY_CONFIGURATION_EDITABLE_FIELD_IDS.participantSessionTimeout ? (
        <>
          <Input
            className='user-session-timeout'
            disabled={savingInlineConfig}
            inline
            id='session-timeout-inline'
            type='number'
            value={sessionDraftMinutes}
            onChange={setSessionDraftMinutes}
            pretext='Timeout after'
            posttext='minutes'
            preventInput={num => num < 0}
            onBlur={event => {
              if (Number(event.target.value) < MIN_SESSION_TIMEOUT_MINUTES) {
                setSessionDraftMinutes(String(MIN_SESSION_TIMEOUT_MINUTES))
              }
            }}
          />
          <div className='config-inline-edit-actions'>
            <Button
              id='save-participant-session-timeout'
              content='Save'
              disabled={savingInlineConfig}
              onClick={saveParticipantSessionInline}
            />
            <Button
              link
              id='cancel-participant-session-timeout'
              content='Cancel'
              disabled={savingInlineConfig}
              onClick={cancelInlineConfigEdit}
            />
          </div>
        </>
      ) : (
        renderTimeoutInput()
      )}
    </Container>
  )

  const ScheduleAndRewardsSection = (
    <Container>
      <h3>Schedule & Rewards</h3>
      <RewardConfig {...configProps} />
      <div className='reward-eligibility'>
        {has_dynamic_cycles ? <DynamicCycleRewardConfig {...configProps} /> : <CycleRewardConfig {...configProps} />}
        {hasGemsRewards ? (
          <BinaryConfig
            {...props}
            bodyText={STRINGS.midtermRewardsBody}
            buttonIcons={{
              leftIcon: richButtonIconMap.iconContainer(supriseBox),
              rightIcon: richButtonIconMap.iconContainer(gem),
            }}
            buttonLabels={{ left: STRINGS.specialRewards, right: STRINGS.avatarItems }}
            configKey={STUDY_CONFIG_MAP.superGems}
            defaultIsFalse
            heading={STRINGS.midtermRewards}
            subheading={STRINGS.midtermRewardsSubheading}
            updateStudyConfig={updateStudyConfig}
            deleteConfigKey={deleteConfigKey}
            isRewardType
          />
        ) : null}
      </div>
    </Container>
  )
  const GiftCardSection = (
    <Container>
      <h3>{STRINGS.giftcardConfig}</h3>
      <GiftCardConfig {...configProps} hasDynamicCycles={has_dynamic_cycles} />
    </Container>
  )

  const RewardsSection = (
    <Container>
      <h3>{STRINGS.rewards}</h3>
      <NewRewardsConfig {...configProps} updateStudyConfig={updateStudyConfig} configObj={configObj} />
    </Container>
  )

  const RewardSelectionSection = (
    <Container>
      <h3>{STRINGS.rewardSelection}</h3>
      <RewardSelectionConfig {...configProps} />
    </Container>
  )

  const SecuritySection = (
    <Container>
      <h3>{STRINGS.security}</h3>
      <SecurityConfig
        {...configProps}
        updateStudySecurityConfig={newConfig => updateStudyConfig({ admin_security: { ...newConfig } })}
        renderTimeoutInput={renderTimeoutInput}
      />
    </Container>
  )

  const ToneSection = (
    <Container>
      <BinaryConfig
        {...props}
        bodyText={STRINGS.toneOfVoiceBody}
        buttonIcons={{ leftIcon: richButtonIconMap.squareCheck, rightIcon: richButtonIconMap.confetti }}
        buttonLabels={{ left: STRINGS.neutral, right: STRINGS.celebratory }}
        configKey={STUDY_CONFIG_MAP.toneOfVoice}
        defaultIsFalse
        heading={STRINGS.toneOfVoice}
        updateStudyConfig={updateStudyConfig}
      />
    </Container>
  )

  const DataSyncSection = (
    <Container>
      <BinaryConfig
        {...props}
        bodyText={STRINGS.dataSyncConfigBody}
        buttonIcons={{ leftIcon: richButtonIconMap.ban, rightIcon: richButtonIconMap.dataSync }}
        buttonLabels={{ left: STRINGS.hide, right: STRINGS.show }}
        configKey={STUDY_CONFIG_MAP.dataSyncUI}
        heading={STRINGS.dataSync}
        updateStudyConfig={updateStudyConfig}
      />
      <ParticipantOfflineDuration study={study} disabled={disabled} />
    </Container>
  )

  return (
    <>
      {!disabled ? (
        <div className='review-section'>
          <h3 className='main-heading'>Review your study</h3>
          {NameSection}
          {MainFeaturesReadOnlySection}
          {renderParticipantReadOnlySection()}
          {ScheduleAndRewardsReadOnlySection}
        </div>
      ) : (
        <>
          {NameSection}
          {isFlagsmithConfigurationSectionShown && <FlagsmithConfigurationSection userId={userId} study={study} />}
          {hasStudyPromotion ? (
            <StudyVersionDetailsSection
              study={study}
              openModal={openModal}
              closeModal={closeModal}
              updateStudy={updateStudy}
              studyVersionList={studyVersionList}
              fetchStudyVersionList={fetchStudyVersionList}
              promoteStudyToUat={promoteStudyToUat}
              promoteStudyToLive={promoteStudyToLive}
              saveStudyVersionNotes={saveStudyVersionNotes}
              hasAccessToEditVersionNotes={hasAccessToEditVersionNotes}
              studyVersionID={studyVersionID}
              showError={showError}
            />
          ) : null}
          {MainFeaturesSection}
          {ParticipantSection}
          {ToneSection}
          {DataSyncSection}
          {SecuritySection}
          {configObj.cycles && ScheduleAndRewardsSection}
          {hasGemsRewards && configObj.super_gems && RewardsSection}
          {hasGemsRewards &&
            !disabled &&
            configObj.super_gems &&
            giftCardSelected &&
            !configObj.super_gems.gift_cards &&
            GiftCardSection}
          {configObj.super_gems && giftCardSelected && configObj.super_gems.gift_cards && RewardSelectionSection}
        </>
      )}
      <CreateStudyFooter {...props} />
    </>
  )
}

CreateStudyReview.propTypes = {
  createStudy: PropTypes.func,
  deleteStudy: PropTypes.func,
  disabled: PropTypes.bool,
  fetchLocales: PropTypes.func,
  hasDeleteStudyPermissions: PropTypes.bool,
  isRootOrDchAdmin: PropTypes.bool,
  isWizard: PropTypes.bool,
  saveAttempted: PropTypes.bool,
  setNavBarProps: PropTypes.func,
  study: PropTypes.shape({
    self_sign_code: PropTypes.string,
    name: PropTypes.string,
    platform_name: PropTypes.string,
    id: PropTypes.number,
  }),
  updateConfig: PropTypes.func,
  updateStudy: PropTypes.func,
  userId: PropTypes.number,
  patchStudy: PropTypes.func,
  fetchStudy: PropTypes.func,
  canEditStudyConfiguration: PropTypes.bool,
  studyLock: PropTypes.bool,
}

export default CreateStudyReview
